// 🎯 智能价格波动函数 - 渐进式引导到预设的endPrice
export function smartPriceFluctuation(currentPrice, orderInfo = {}, remainingTime = 0, totalTime = 0) {
    const price = parseFloat(currentPrice);
    const { resultType, type, buyPrice, endPrice } = orderInfo;
    const buyPriceNum = parseFloat(buyPrice);
    const targetPrice = parseFloat(endPrice); // 预设的目标结算价格

    // 🎯 获取价格精度和显示精度
    let decimalPlaces = 4; // 默认4位小数
    let displayDecimalPlaces = 2; // 默认显示2位小数

    if (buyPrice) {
        const buyPriceStr = buyPrice.toString();
        if (buyPriceStr.includes('.')) {
            decimalPlaces = Math.max(4, buyPriceStr.split('.')[1].length);
            // 显示精度通常是2位小数，除非原始精度更高
            displayDecimalPlaces = Math.min(decimalPlaces, buyPriceStr.split('.')[1].length);
        }
    }

    // 🎯 智能波动单位计算 - 根据价格大小和显示精度动态调整
    const baseFluctuation = calculateSmartFluctuation(buyPriceNum, displayDecimalPlaces);

    // 🎯 计算时间进度 (0-1)
    const timeProgress = totalTime > 0 ? Math.max(0, Math.min(1, (totalTime - remainingTime) / totalTime)) : 0;

    // 🎯 渐进式价格引导策略
    let direction = calculateProgressiveDirection(
        price, buyPriceNum, targetPrice, resultType, type,
        remainingTime, timeProgress, decimalPlaces
    );

    // 🎯 动态波动幅度：早期小幅波动，后期精确引导
    const steps = getProgressiveSteps(timeProgress, remainingTime, buyPriceNum);
    const fluctuation = baseFluctuation * steps;

    // 计算新价格
    const newPrice = price + (fluctuation * direction);

    // 确保价格为正数
    const finalPrice = newPrice > 0 ? newPrice : price + Math.abs(fluctuation);

    // 🎯 保持精度
    return parseFloat(finalPrice.toFixed(decimalPlaces));
}

// 🎯 智能波动单位计算函数
function calculateSmartFluctuation(price, displayDecimalPlaces) {
    const priceNum = parseFloat(price) || 1;

    // 🎯 根据价格大小确定波动基数
    let baseUnit;
    if (priceNum >= 1000) {
        // 价格 >= 1000：波动单位 0.1-1.0
        baseUnit = 0.1;
    } else if (priceNum >= 100) {
        // 价格 100-999：波动单位 0.01-0.1
        baseUnit = 0.01;
    } else if (priceNum >= 10) {
        // 价格 10-99：波动单位 0.001-0.01
        baseUnit = 0.001;
    } else if (priceNum >= 1) {
        // 价格 1-9：波动单位 0.0001-0.001
        baseUnit = 0.0001;
    } else {
        // 价格 < 1：波动单位 0.00001-0.0001
        baseUnit = 0.00001;
    }

    // 🎯 根据显示精度调整波动单位
    const displayUnit = Math.pow(10, -displayDecimalPlaces);

    // 🎯 确保波动单位至少是显示精度的1倍，最多是10倍
    const minFluctuation = displayUnit;
    const maxFluctuation = displayUnit * 10;

    // 🎯 在合理范围内选择波动单位
    let smartFluctuation = Math.max(baseUnit, minFluctuation);
    smartFluctuation = Math.min(smartFluctuation, maxFluctuation);

    console.log(`🎯 [智能波动] 价格: ${priceNum}, 显示精度: ${displayDecimalPlaces}, 基础单位: ${baseUnit}, 最终波动单位: ${smartFluctuation}`);

    return smartFluctuation;
}

// 🎯 渐进式方向计算函数 - 智能引导到预设的endPrice
function calculateProgressiveDirection(currentPrice, buyPrice, targetPrice, resultType, orderType, remainingTime, timeProgress, decimalPlaces) {
    // 如果没有目标价格，使用传统逻辑
    if (!targetPrice || targetPrice === 0) {
        return calculateLegacyDirection(resultType, orderType, currentPrice, buyPrice, remainingTime);
    }

    const currentPriceNum = parseFloat(currentPrice);
    const buyPriceNum = parseFloat(buyPrice);
    const targetPriceNum = parseFloat(targetPrice);

    // 🎯 计算到目标价格的距离
    const distanceToTarget = targetPriceNum - currentPriceNum;
    const distancePercent = Math.abs(distanceToTarget) / buyPriceNum;

    // 🎯 阶段性策略
    if (timeProgress < 0.3) {
        // 🎯 早期阶段（0-30%）：主要随机波动，偶尔朝向目标
        return getEarlyStageDirection(distanceToTarget, distancePercent);
    } else if (timeProgress < 0.8) {
        // 🎯 中期阶段（30-80%）：逐渐增加朝向目标的概率
        return getMidStageDirection(distanceToTarget, distancePercent, timeProgress);
    } else {
        // 🎯 后期阶段（80-100%）：强力引导到目标价格
        return getLateStageDirection(distanceToTarget, distancePercent, remainingTime);
    }
}

// 🎯 早期阶段方向计算（主要随机）
function getEarlyStageDirection(distanceToTarget, distancePercent) {
    // 如果距离目标太远（>0.05%），稍微朝向目标
    if (distancePercent > 0.0005) {
        return Math.random() < 0.3 ? Math.sign(distanceToTarget) : (Math.random() > 0.5 ? 1 : -1);
    }
    // 否则完全随机
    return Math.random() > 0.5 ? 1 : -1;
}

// 🎯 中期阶段方向计算（逐渐引导）
function getMidStageDirection(distanceToTarget, distancePercent, timeProgress) {
    // 根据时间进度增加朝向目标的概率：30% -> 70%
    const targetProbability = 0.3 + (timeProgress - 0.3) * 0.8; // 0.3 + 0.4 * progress

    if (Math.abs(distanceToTarget) < Math.pow(10, -4)) {
        // 已经很接近目标，随机小幅波动
        return Math.random() > 0.5 ? 1 : -1;
    }

    return Math.random() < targetProbability ? Math.sign(distanceToTarget) : (Math.random() > 0.5 ? 1 : -1);
}

// 🎯 后期阶段方向计算（强力引导）
function getLateStageDirection(distanceToTarget, distancePercent, remainingTime) {
    // 最后10秒，强制朝向目标
    if (remainingTime <= 10) {
        return Math.sign(distanceToTarget) || (Math.random() > 0.5 ? 1 : -1);
    }

    // 最后20%时间，90%概率朝向目标
    if (Math.abs(distanceToTarget) < Math.pow(10, -4)) {
        // 已经很接近目标，小幅随机波动
        return Math.random() > 0.5 ? 1 : -1;
    }

    return Math.random() < 0.9 ? Math.sign(distanceToTarget) : (Math.random() > 0.5 ? 1 : -1);
}

// 🎯 动态波动幅度计算 - 根据价格和时间阶段调整
function getProgressiveSteps(timeProgress, remainingTime, price) {
    const priceNum = parseFloat(price) || 1;

    // 🎯 根据价格大小调整波动倍数范围
    let minSteps, maxSteps;
    if (priceNum >= 100) {
        // 高价格：更大的波动倍数
        minSteps = 2;
        maxSteps = 8;
    } else if (priceNum >= 10) {
        // 中等价格：中等波动倍数
        minSteps = 1;
        maxSteps = 5;
    } else {
        // 低价格：较小的波动倍数
        minSteps = 1;
        maxSteps = 3;
    }

    // 🎯 根据时间阶段调整波动强度
    if (timeProgress < 0.3) {
        // 早期：较大的随机波动
        return Math.floor(Math.random() * (maxSteps - minSteps + 1)) + minSteps;
    } else if (timeProgress < 0.8) {
        // 中期：中等的稳定波动
        const midMax = Math.ceil((maxSteps + minSteps) / 2);
        return Math.floor(Math.random() * (midMax - minSteps + 1)) + minSteps;
    } else if (remainingTime <= 10) {
        // 后期：精确的小幅波动
        return minSteps; // 最后阶段精确控制
    } else {
        // 其他情况：小幅波动
        return Math.floor(Math.random() * (minSteps + 1)) + minSteps;
    }
}

// 🎯 传统方向计算函数（备用）
function calculateLegacyDirection(resultType, orderType, currentPrice, buyPrice, remainingTime) {
    if (resultType === null || resultType === undefined || !orderType) {
        return Math.random() > 0.5 ? 1 : -1;
    }

    const currentPriceNum = parseFloat(currentPrice);
    const buyPriceNum = parseFloat(buyPrice);

    // 最终阶段控制
    if (remainingTime <= 10 && remainingTime > 0) {
        return getFinalDirection(resultType, orderType, currentPriceNum, buyPriceNum);
    }

    // 正常波动阶段
    const isRandomMove = Math.random() < 0.6;
    if (isRandomMove) {
        return Math.random() > 0.5 ? 1 : -1;
    } else {
        return getTargetDirection(resultType, orderType, currentPriceNum, buyPriceNum);
    }
}

// 🎯 获取最终方向（确保结果正确）
function getFinalDirection(resultType, orderType, currentPrice, buyPrice) {
    const priceDiff = currentPrice - buyPrice;

    if (resultType === 0 || resultType === 2) { // 包输
        if (orderType === 1) { // 买涨但要输
            return priceDiff > 0 ? -1 : (Math.random() < 0.3 ? 1 : -1); // 确保最终下跌
        } else { // 买跌但要输
            return priceDiff < 0 ? 1 : (Math.random() < 0.3 ? -1 : 1); // 确保最终上涨
        }
    } else if (resultType === 1) { // 包赢
        if (orderType === 1) { // 买涨且要赢
            return priceDiff < 0 ? 1 : (Math.random() < 0.3 ? -1 : 1); // 确保最终上涨
        } else { // 买跌且要赢
            return priceDiff > 0 ? -1 : (Math.random() < 0.3 ? 1 : -1); // 确保最终下跌
        }
    }

    return Math.random() > 0.5 ? 1 : -1;
}

// 🎯 获取目标方向（缓慢朝向目标）
function getTargetDirection(resultType, orderType, currentPrice, buyPrice) {
    if (resultType === 0) { // 包输
        if (orderType === 1) { // 买涨但要输 -> 缓慢下跌
            return -1;
        } else { // 买跌但要输 -> 缓慢上涨
            return 1;
        }
    } else if (resultType === 1) { // 包赢
        if (orderType === 1) { // 买涨且要赢 -> 缓慢上涨
            return 1;
        } else { // 买跌且要赢 -> 缓慢下跌
            return -1;
        }
    }

    return Math.random() > 0.5 ? 1 : -1;
}

// 🎯 智能方向生成（带反弹机制）
function getSmartDirection(preferredDirection, probability) {
    // 使用概率决定方向，但保留反弹可能性
    return Math.random() < probability ? preferredDirection : -preferredDirection;
}

// 兼容旧版本的随机价格函数
export function randomNumber(number) {
    return smartPriceFluctuation(number, {});
}

// 生成最终结算价格 - 用于期权订单结算时的价格计算（4位小数精度）
export function generateFinalPrice(basePrice, isWinning = null) {
    const price = parseFloat(basePrice);

    // 计算基于4位小数精度的合理波动（比实时波动稍大）
    let minFluctuation, maxFluctuation;

    if (price < 1) {
        // 小于1美元：波动0.0002-0.0020
        minFluctuation = 0.0002;
        maxFluctuation = 0.0020;
    } else if (price < 10) {
        // 1-10美元：波动0.0002-0.0050
        minFluctuation = 0.0002;
        maxFluctuation = 0.0050;
    } else if (price < 100) {
        // 10-100美元：波动0.0020-0.0200
        minFluctuation = 0.0020;
        maxFluctuation = 0.0200;
    } else if (price < 1000) {
        // 100-1000美元：波动0.0200-0.2000
        minFluctuation = 0.0200;
        maxFluctuation = 0.2000;
    } else {
        // 大于1000美元：波动0.2000-2.0000
        minFluctuation = 0.2000;
        maxFluctuation = 2.0000;
    }

    // 生成随机波动值
    const fluctuationRange = maxFluctuation - minFluctuation;
    const fluctuation = minFluctuation + Math.random() * fluctuationRange;

    // 确定波动方向
    let direction;
    if (isWinning === true) {
        // 盈利倾向：70%概率朝有利方向波动
        direction = Math.random() > 0.3 ? 1 : -1;
    } else if (isWinning === false) {
        // 亏损倾向：70%概率朝不利方向波动
        direction = Math.random() > 0.3 ? -1 : 1;
    } else {
        // 随机方向
        direction = Math.random() > 0.5 ? 1 : -1;
    }

    // 计算最终价格
    const finalPrice = price + (fluctuation * direction);

    // 确保价格为正数
    const result = finalPrice > 0 ? finalPrice : price - fluctuation;

    // 统一保留4位小数
    return parseFloat(result.toFixed(4));
}

// 缺省走势图的数据
export function randomLine() {
    const initialPrice=52100;
    // 生成100个随机数，模拟价格的变动，使得趋势相对平缓
    const randomChanges=Array.from({ length: 99 }, () => (Math.random()-0.5)*10);
    // 计算每个价格点
    const prices=randomChanges.reduce((acc, randomChange) => {
        const lastPrice=acc[acc.length-1];
        const newPrice=lastPrice+randomChange
        return [...acc, newPrice];
    }, [initialPrice]);
    return prices
}

// 🎯 WhatsApp 客服号码（统一配置）
const WHATSAPP_NUMBER = '16465676110';

/**
 * 检测是否在钱包 dApp 浏览器环境中
 * 常见钱包：MetaMask, Trust Wallet, TokenPocket, imToken, Coinbase Wallet 等
 */
function isWalletBrowser() {
    const ua = navigator.userAgent.toLowerCase();
    return (
        // MetaMask
        ua.includes('metamask') ||
        // Trust Wallet
        ua.includes('trust') ||
        // TokenPocket
        ua.includes('tokenpocket') ||
        // imToken
        ua.includes('imtoken') ||
        // Coinbase Wallet
        ua.includes('coinbasewallet') ||
        // BitKeep
        ua.includes('bitkeep') ||
        // 通用 WebView 检测（可能是钱包内置浏览器）
        (ua.includes('wv') && (ua.includes('android') || ua.includes('iphone'))) ||
        // 检测是否有 ethereum 或 web3 注入（表明在 dApp 环境）
        typeof window.ethereum !== 'undefined' ||
        typeof window.web3 !== 'undefined'
    );
}

/**
 * 检测是否是移动设备
 */
function isMobileDevice() {
    return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
}

/**
 * 打开 WhatsApp 联系客服
 * 兼容多种设备和环境：
 * - 手机浏览器（Safari、Chrome 等）
 * - PC 端浏览器
 * - 钱包 dApp 浏览器（MetaMask、Trust Wallet、TokenPocket 等）
 *
 * @param {string} message 可选的预填消息
 */
export function openWhatsApp(message = '') {
    const phone = WHATSAPP_NUMBER;
    const encodedMessage = message ? encodeURIComponent(message) : '';

    // 🎯 使用 api.whatsapp.com 作为主要链接（兼容性最好）
    // 此链接在移动端会自动尝试打开 WhatsApp APP，在 PC 端会打开 Web WhatsApp
    const apiUrl = `https://api.whatsapp.com/send?phone=${phone}${encodedMessage ? '&text=' + encodedMessage : ''}`;

    const isMobile = isMobileDevice();
    const isWallet = isWalletBrowser();

    console.log(`[WhatsApp] 环境检测: isMobile=${isMobile}, isWallet=${isWallet}`);

    // 🎯 策略说明：
    // 1. 钱包 dApp 浏览器：直接使用 location.href，因为 window.open 通常被阻止
    // 2. 移动端浏览器：优先使用 location.href 打开（会触发系统选择器打开 WhatsApp）
    // 3. PC 端浏览器：使用 window.open 在新标签页打开

    if (isWallet) {
        // 🎯 钱包 dApp 环境：直接跳转，避免 window.open 被阻止
        console.log('[WhatsApp] 检测到钱包环境，使用 location.href');
        window.location.href = apiUrl;
        return;
    }

    if (isMobile) {
        // 🎯 移动端浏览器：直接跳转
        // api.whatsapp.com 会自动检测并尝试打开 WhatsApp APP
        // 如果未安装，会显示下载页面
        console.log('[WhatsApp] 移动端浏览器，使用 location.href');
        window.location.href = apiUrl;
        return;
    }

    // 🎯 PC 端浏览器：在新标签页打开
    console.log('[WhatsApp] PC 端浏览器，使用 window.open');
    try {
        const newWindow = window.open(apiUrl, '_blank', 'noopener,noreferrer');
        // 检查是否被浏览器阻止
        if (!newWindow || newWindow.closed || typeof newWindow.closed === 'undefined') {
            console.log('[WhatsApp] 弹窗被阻止，降级使用 location.href');
            window.location.href = apiUrl;
        }
    } catch (e) {
        console.error('[WhatsApp] 打开失败:', e);
        window.location.href = apiUrl;
    }
}